import React, { useState, useEffect, useCallback } from 'react';
import L from 'leaflet';
import { connect } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { Map, TileLayer, Marker } from 'react-leaflet';
import EntryMarker from './Markers/EntryMarker';
import goNew from '../assets/images/goNew.png';
import './LeafletMap.css';
import {
  toggleDisplays,
  toggleShip
} from '../state/uiControl/uiControl.actions';
import {
  loadShipsRequest
} from '../state/shipLocations/shipLocations.actions';
import PositionMarkers from './Markers/PositionMarkers';
import { isProd } from '../util';
import SelectedMarker from './Markers/SelectedMarker';
import { ShipMarker } from './Markers/ShipMarker';
// import PortMarker from './Markers/PortMarker';
import { Display } from '../state/uiControl/uiControl.reducer';
import ItineraryMarker from './Markers/ItineraryMarker';
import { Entry } from '../services/types';
import { useIsMobile, useWindowDimensions } from '../hooks';
import { closedDrawerHeight } from './ExploreDrawer';

const goNewIcon = new L.Icon({
  iconUrl: goNew,
  iconSize: [230, 30],
});

// offset values added to marker longitude values to fix missing marker problems
export const longitudeOffsets = [0, 360, 720, -360];

// given a longitude value, return its equivalent on a 0-360 scale
const getConvertedLong = (long: number) => {
  // only applies if coordinates are near the edge of the map (-180/180)
  if (long < -120) {
    return 180 + (180 - Math.abs(long));
  }
  return long;
};

// given a value on the 360 degree scale, return standard longitude equivalent
const getStandardLong = (long: number) => long > 180 ? long - 360 : long;

const corner1 = L.latLng(-90, -99999);
const corner2 = L.latLng(90, 99999);
const bounds = L.latLngBounds(corner1, corner2);

const goSomewhereNewLink = 'https://www.seabourn.com/en/us/find-a-cruise';

let shipRetry = 2;
// const isFeed = process.env.REACT_APP_SHOW_FEEDS;
export const LeafletMap: React.FC<LeafletProps> = (props) => {
  const {
    tempSelectedEntry,
    selectedEntries,
    shipLocations,
    loadShipsRequest,
    isEntryShowing,
    toggleShip,
    userData
  } = props;

  // const [hasTiles, updateHasTiles] = useState(false);
  // const [restrictedTiles, setRestrictedTiles] = useState();
  // const [allTiles, setAllTiles] = useState();

  const [mapInstance, setMapInstance] = useState(null);
  const [dotFrequency, setDotFrequency] = useState(6); //1= every time, 3 is every 3 times, ect.
  const [eventSet, toggleEventSet] = useState(false);

  const isMobile = useIsMobile();
  const { innerWidth, innerHeight } = useWindowDimensions();
  const infoBoxElement = document.querySelector("#box");

  /**
   * Reference: https://github.com/Leaflet/Leaflet/issues/859#issuecomment-24879573
   */
  const setViewOffset = useCallback((latlng: L.LatLngExpression, offset: L.PointExpression, targetZoom: number) => {
    const map: any = mapInstance;

    const targetPoint = map.leafletElement.project(latlng, targetZoom).subtract(offset);
    const targetLatLng = map.leafletElement.unproject(targetPoint, targetZoom);
    return map.leafletElement.setView(targetLatLng, targetZoom);
  }, [mapInstance]);

  if ((!shipLocations || shipLocations.length === 0) && shipRetry > 0) {
    loadShipsRequest();
    shipRetry--;
  }

  //Sets the tile layers
  // useEffect(() => {
  //   if (mapInstance && mapInstance.leafletElement && !hasTiles) {
  //     // let restrictedTilesLayer = L.tileLayer('https://api.maptiler.com/tiles/satellite/{z}/{x}/{y}.jpg?key=Vt2Yoyj3uzfCJurAI26b', {
  //     //   minNativeZoom: 0,
  //     //   maxNativeZoom: 3
  //     // });
  //     // setRestrictedTiles(restrictedTilesLayer);
  //     // let allTilesLayer = L.tileLayer('https://api.maptiler.com/tiles/satellite/{z}/{x}/{y}.jpg?key=Vt2Yoyj3uzfCJurAI26b');
  //     // setAllTiles(allTilesLayer);
  //     // allTilesLayer.addTo(mapInstance.leafletElement);
  //     // updateHasTiles(true);

  //     // mapInstance.leafletElement.on('move', function (e: any) {
  //     //   var zoomlevel = mapInstance.leafletElement.getZoom();
  //     //   var center = mapInstance.leafletElement.getCenter();

  //     //   if (zoomlevel > 3 && center.lat < -57) {
  //     //     if (mapInstance.leafletElement.hasLayer(allTilesLayer)) {
  //     //       mapInstance.leafletElement.removeLayer(allTilesLayer);
  //     //       restrictedTilesLayer.addTo(mapInstance.leafletElement);
  //     //     }
  //     //   } else {

  //     //     if (mapInstance.leafletElement.hasLayer(restrictedTilesLayer)) {
  //     //       mapInstance.leafletElement.removeLayer(restrictedTilesLayer);
  //     //       allTilesLayer.addTo(mapInstance.leafletElement);
  //     //     }
  //     //   }
  //     // });
  //   }
  // });

  useEffect(() => {
    const map: any = mapInstance;
    if (map && map.leafletElement && !eventSet) {
      map.leafletElement.on('zoom', function (e: any) {
        const zoomlevel = map.leafletElement.getZoom();
        if (zoomlevel > 6 && map.leafletElement.dot !== 1) {
          map.leafletElement.dot = 1;
          setDotFrequency(1);
        } else if (zoomlevel > 4 && zoomlevel <= 6 && map.leafletElement.dot !== 3) {
          setDotFrequency(3);
          map.leafletElement.dot = 3;
        } else if (zoomlevel <= 4 && map.leafletElement.dot !== 6) {
          setDotFrequency(6);
          map.leafletElement.dot = 6;
        }
      });
      toggleEventSet(true);
    }
  }, [mapInstance, eventSet]);

  //Moves the map to the first blog entry point when a new voyage is selected
  useEffect(() => {
    const map: any = mapInstance;
    if (map && map.leafletElement && selectedEntries[0] && (infoBoxElement || isMobile)) {
      const longitudes = selectedEntries
        .filter(entry => entry.long !== undefined)
        .map(entry => getConvertedLong(entry.long!));
      const latitudes = selectedEntries
        .filter(entry => entry.lat !== undefined)
        .map(entry => entry.lat!);
      const centerLong = getStandardLong((Math.max(...longitudes) + Math.min(...longitudes)) / 2);
      const centerLat = (Math.max(...latitudes) + Math.min(...latitudes)) / 2;
      const entryBoxWidth = infoBoxElement?.clientWidth;
      const offsetLong = (entryBoxWidth ? (entryBoxWidth + 216) / 2 : innerWidth / 3);
      
      setViewOffset(
        [centerLat, centerLong],
        isMobile ? [0, -closedDrawerHeight] : [-offsetLong, 0],
        3
      );
    }
  }, [infoBoxElement, innerHeight, innerWidth, isMobile, mapInstance, selectedEntries, setViewOffset]);

  let selectedEntry: null | Entry & {long: number; lat: number} = null;
  if (
    tempSelectedEntry &&
    tempSelectedEntry.lat !== undefined &&
    tempSelectedEntry.long !== undefined
  ) {
    selectedEntry = {
      ...tempSelectedEntry,
      lat: tempSelectedEntry.lat,
      long: tempSelectedEntry.long
    };
  }

  return (
    <Map
      className="map"
      ref={(map: any) => { if ((mapInstance === undefined || mapInstance === null) && map !== null) setMapInstance(map); }}
      center={[selectedEntries[0] ? selectedEntries[0].lat! : 12, selectedEntries[0] ? selectedEntries[0].long! : -38]}
      maxBounds={bounds}
      zoom={isMobile ? 2 : 3}
      worldCopyJump={true}
      maxZoom={9}
      minZoom={isMobile ? 2 : 3}
      zoomControl={false}
      preferCanvas={true}
    >
      <TileLayer url={'https://api.maptiler.com/tiles/satellite/{z}/{x}/{y}.jpg?key=pWw1XMl17cDjy3d3AEwZ'} />

      {shipLocations && shipLocations.map((ship, index) => {
        return (
          <ShipMarker
            key={ship.ShipName}
            ship={ship}
            clickHandler={() => toggleShip(ship)}
            index={index}
          />
        );
      })}
      <PositionMarkers
        dotFrequency={dotFrequency}
      />

      {selectedEntries[0] &&
        selectedEntries.map((entry: Entry, index: number) => (
          <EntryMarker
            key={entry.id}
            locationID={index}
            entry={entry}
          />
        ))}
      {!isProd && <ItineraryMarker />}
      {/* {!isProd && <PortMarker/>} */}

      {selectedEntry && isEntryShowing &&
        <SelectedMarker selectedEntry={selectedEntry} />
      }
      {userData && <React.Fragment>
        <Marker
          entry={selectedEntry}
          position={[
            -59.442635, -63.613951
          ]}
          zIndexOffset={235}
          icon={goNewIcon}
          riseOnHover={true}
          onClick={() => window.open(goSomewhereNewLink, '_blank')}
        />
        <Marker
          entry={selectedEntry}
          position={[
            -59.442635, -423.613951
          ]}
          zIndexOffset={235}
          icon={goNewIcon}
          riseOnHover={true}
          onClick={() => window.open(goSomewhereNewLink, '_blank')}
        />
      </React.Fragment>
      }
    </Map>
  );
};

// 'https://api.maptiler.com/tiles/satellite/{z}/{x}/{y}.jpg?key=Vt2Yoyj3uzfCJurAI26b'
// 'https://hoyluengmatt.blob.core.windows.net/suffolk-tiles/{z}/{x}/{y}.png'

const mapStateToProps = (state: RootState) => ({
  tempSelectedEntry: state.uiControl.selectedEntry,
  selectedEntries: state.voyagesData.selectedVoyageEntries,
  shipLocations: state.shipLocations.shipLocations,
  isEntryShowing: state.uiControl.showing === Display.ENTRY,
  userData: state.user.userData,

});

const dispatchProps = {
  toggleDisplays,
  loadShipsRequest,
  toggleShip
};

type LeafletProps = ReturnType<typeof mapStateToProps> & typeof dispatchProps;

export default connect(
  mapStateToProps,
  dispatchProps
)(LeafletMap);